import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { Settings as SettingsIcon, Users, Link2, Building2, CreditCard, Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { errorMessage, toError } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { ReferralCard } from '@/components/referrals/ReferralCard';

const settingsTabs = [
  { id: 'workspace', label: 'Workspace', icon: Building2, path: '/app/settings' },
  { id: 'team', label: 'Team', icon: Users, path: '/app/settings/team' },
  { id: 'integrations', label: 'Integrations', icon: Link2, path: '/app/settings/integrations' },
  { id: 'billing', label: 'Billing', icon: CreditCard, path: '/app/settings/billing' },
];

const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Dubai',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
];

const CURRENCIES = [
  { code: 'USD', label: 'US Dollar ($)' },
  { code: 'EUR', label: 'Euro (€)' },
  { code: 'GBP', label: 'British Pound (£)' },
  { code: 'BRL', label: 'Brazilian Real (R$)' },
  { code: 'CAD', label: 'Canadian Dollar (C$)' },
  { code: 'AUD', label: 'Australian Dollar (A$)' },
  { code: 'JPY', label: 'Japanese Yen (¥)' },
  { code: 'CNY', label: 'Chinese Yuan (¥)' },
  { code: 'INR', label: 'Indian Rupee (₹)' },
  { code: 'MXN', label: 'Mexican Peso ($)' },
];

const initialFormState = { name: '', timezone: 'UTC', currency: 'USD' };

export default function Settings() {
  const { workspace, loading } = useWorkspace();
  const { refetchWorkspaces } = useWorkspaceScope();
  const [formData, setFormData] = useState(initialFormState);

  const { data: workspaceRow } = useQuery({
    queryKey: ['workspace-settings', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return null;
      try {
        const { data, error } = await supabase
          .from('workspaces')
          .select('name, timezone, currency')
          .eq('id', workspace.id)
          .single();
        if (error) return { name: (data as any)?.name ?? (workspace as { name?: string })?.name ?? '', timezone: 'UTC', currency: 'USD' };
        return data as { name: string; timezone: string | null; currency: string | null };
      } catch {
        return { name: (workspace as { name?: string })?.name ?? '', timezone: 'UTC', currency: 'USD' };
      }
    },
    enabled: !!workspace?.id,
  });

  const initialData = useMemo(() => {
    if (!workspaceRow) return initialFormState;
    return {
      name: workspaceRow.name || '',
      timezone: workspaceRow.timezone || 'UTC',
      currency: workspaceRow.currency || 'USD',
    };
  }, [workspaceRow]);

  useEffect(() => {
    if (workspaceRow) {
      setFormData({
        name: workspaceRow.name || '',
        timezone: workspaceRow.timezone || 'UTC',
        currency: workspaceRow.currency || 'USD',
      });
    }
  }, [workspaceRow]);

  const isDirty = useMemo(() => {
    return (
      formData.name.trim() !== initialData.name ||
      formData.timezone !== initialData.timezone ||
      formData.currency !== initialData.currency
    );
  }, [formData, initialData]);

  const updateMutation = useMutation({
    mutationFn: async (data: { name: string; timezone: string; currency: string }) => {
      if (!workspace?.id) throw new Error('No workspace');
      const { error } = await supabase
        .from('workspaces')
        .update({
          name: data.name.trim(),
          timezone: data.timezone,
          currency: data.currency,
        })
        .eq('id', workspace.id);
      if (error) throw toError(error, 'Failed to update workspace settings');
    },
    onSuccess: async () => {
      await refetchWorkspaces();
      toast({ title: 'Settings saved', description: 'Your workspace settings have been updated.' });
    },
    onError: (error: unknown) => {
      const err = error as Error & { code?: string };
      const isPermissionDenied =
        err?.code === '42501' ||
        err?.code === 'PGRST301' ||
        err?.message?.includes('permission') ||
        err?.message?.includes('RLS') ||
        err?.message?.includes('row-level security');
      if (isPermissionDenied) {
        toast({
          title: 'Permission denied',
          description: "You don't have permission to update workspace settings. Ask the workspace owner or admin.",
          variant: 'destructive',
        });
      } else {
        toast({ title: 'Error', description: errorMessage(error, 'Failed to save settings'), variant: 'destructive' });
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast({ title: 'Error', description: 'Workspace name is required.', variant: 'destructive' });
      return;
    }
    updateMutation.mutate(formData);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <SettingsIcon className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Settings</h1>
          <p className="text-muted-foreground text-sm">
            Manage your workspace and integrations
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-8 border-b">
        {settingsTabs.map((tab) => (
          <Link
            key={tab.id}
            to={tab.path}
            className={cn(
              'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab.path === '/app/settings'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </Link>
        ))}
      </div>

      {/* Workspace Settings Content */}
      <div className="max-w-2xl">
        <div className="bg-card border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Workspace Settings</h2>
          <p className="text-muted-foreground text-sm mb-6">
            Manage your workspace details and preferences.
          </p>

          {loading || (!!workspace?.id && workspaceRow === undefined) ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Workspace Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="My Workspace"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  This name is visible to all team members.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone</Label>
                <Select
                  value={formData.timezone}
                  onValueChange={(v) => setFormData({ ...formData, timezone: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz} value={tz}>
                        {tz.replace(/_/g, ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Used for scheduling and reporting.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="currency">Default Currency</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(v) => setFormData({ ...formData, currency: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Used for deal values and revenue reporting.
                </p>
              </div>

              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={!isDirty || updateMutation.isPending || !formData.name.trim()}
                >
                  {updateMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <Save className="w-4 h-4 mr-2" />
                  Save changes
                </Button>
              </div>
            </form>
          )}
        </div>

        {/* Referral Program */}
        <div className="mt-8">
          <ReferralCard />
        </div>
      </div>
    </div>
  );
}